import { useCallback, useMemo, useRef, useState } from 'react';
import {
  measureRoomShape,
  remapOpenings,
  roomShapeProblems,
  type RoomFeature,
  type RoomOpening,
  type RoomShape,
} from '@peles/shared';

export type ShapeSelection =
  | { type: 'vertex'; index: number }
  | { type: 'wall'; index: number }
  | { type: 'feature'; id: string }
  | { type: 'opening'; id: string }
  | null;

const HISTORY_LIMIT = 100;

let idCounter = 0;
/** An id for a new feature or opening, unique within the shape. */
export function newShapeItemId(prefix: string): string {
  idCounter += 1;
  const random =
    globalThis.crypto?.randomUUID?.().slice(0, 8) ?? Math.random().toString(36).slice(2, 10);
  return `${prefix}-${random}-${idCounter}`;
}

/**
 * The shape being edited, its selection and its own undo history. Every
 * change goes through `update`, so undo covers typing a wall length, dragging
 * a corner and applying a preset alike. A drag reports many moves; `update`
 * with `coalesce` folds them into the one history step the drag began.
 */
export function useRoomShapeEditor(initial: RoomShape) {
  const [shape, setShape] = useState<RoomShape>(initial);
  const [selection, setSelection] = useState<ShapeSelection>(null);
  // The current shape, readable synchronously: history is kept outside the
  // state updater, which React may call twice.
  const current = useRef<RoomShape>(initial);
  const past = useRef<RoomShape[]>([]);
  const future = useRef<RoomShape[]>([]);
  const [, bumpHistory] = useState(0);
  const coalescing = useRef(false);

  const commit = useCallback((value: RoomShape) => {
    current.current = value;
    setShape(value);
    bumpHistory((n) => n + 1);
  }, []);

  const update = useCallback(
    (next: RoomShape | ((prev: RoomShape) => RoomShape), opts?: { coalesce?: boolean }) => {
      const prev = current.current;
      const value = typeof next === 'function' ? next(prev) : next;
      if (value === prev) return;
      if (!(opts?.coalesce && coalescing.current)) {
        past.current = [...past.current.slice(-(HISTORY_LIMIT - 1)), prev];
        future.current = [];
      }
      coalescing.current = !!opts?.coalesce;
      commit(value);
    },
    [commit],
  );

  /** End a drag: the next coalescing update starts a new history step. */
  const endGesture = useCallback(() => {
    coalescing.current = false;
  }, []);

  const undo = useCallback(() => {
    const previous = past.current.pop();
    if (!previous) return;
    future.current = [current.current, ...future.current];
    coalescing.current = false;
    setSelection(null);
    commit(previous);
  }, [commit]);

  const redo = useCallback(() => {
    const [next, ...rest] = future.current;
    if (!next) return;
    future.current = rest;
    past.current = [...past.current, current.current];
    coalescing.current = false;
    setSelection(null);
    commit(next);
  }, [commit]);

  /** Replace everything (a preset, typed walls, a finished trace). */
  const replaceOutline = useCallback(
    (outline: RoomShape['outline']) => {
      update((prev) => ({
        ...prev,
        outline,
        // A new outline numbers its walls afresh: carry doors by where they are.
        openings: remapOpenings(prev.outline, outline, prev.openings),
      }));
      setSelection(null);
    },
    [update],
  );

  const addFeature = useCallback(
    (feature: Omit<RoomFeature, 'id'>) => {
      const id = newShapeItemId('f');
      update((prev) => ({ ...prev, features: [...prev.features, { ...feature, id }] }));
      setSelection({ type: 'feature', id });
    },
    [update],
  );

  const updateFeature = useCallback(
    (id: string, patch: Partial<RoomFeature>, opts?: { coalesce?: boolean }) =>
      update(
        (prev) => ({
          ...prev,
          features: prev.features.map((f) => (f.id === id ? { ...f, ...patch } : f)),
        }),
        opts,
      ),
    [update],
  );

  const removeFeature = useCallback(
    (id: string) => {
      update((prev) => ({ ...prev, features: prev.features.filter((f) => f.id !== id) }));
      setSelection(null);
    },
    [update],
  );

  const addOpening = useCallback(
    (opening: Omit<RoomOpening, 'id'>) => {
      const id = newShapeItemId('o');
      update((prev) => ({ ...prev, openings: [...prev.openings, { ...opening, id }] }));
      setSelection({ type: 'opening', id });
    },
    [update],
  );

  const updateOpening = useCallback(
    (id: string, patch: Partial<RoomOpening>, opts?: { coalesce?: boolean }) =>
      update(
        (prev) => ({
          ...prev,
          openings: prev.openings.map((o) => (o.id === id ? { ...o, ...patch } : o)),
        }),
        opts,
      ),
    [update],
  );

  const removeOpening = useCallback(
    (id: string) => {
      update((prev) => ({ ...prev, openings: prev.openings.filter((o) => o.id !== id) }));
      setSelection(null);
    },
    [update],
  );

  const measurements = useMemo(() => measureRoomShape(shape), [shape]);
  const problems = useMemo(() => roomShapeProblems(shape), [shape]);

  return {
    shape,
    selection,
    setSelection,
    update,
    endGesture,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
    replaceOutline,
    addFeature,
    updateFeature,
    removeFeature,
    addOpening,
    updateOpening,
    removeOpening,
    measurements,
    problems,
  };
}

export type RoomShapeEditorState = ReturnType<typeof useRoomShapeEditor>;
